// BulkStrategyCard.jsx - WITH HOT STRATEGY DETECTION (1 Signal Away)
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Stack,
  Chip,
  LinearProgress,
  alpha,
  Badge,
  List,
  ListItem,
  ListItemText,
  Paper,
  Collapse,
  Tooltip,
  Button,
  IconButton,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Bolt as BoltIcon,
  Description as DescriptionIcon,
  Dashboard as DashboardIcon,
  Lock as LockIcon,
  History as HistoryIcon,
  WbSunny as WbSunnyIcon,
  Nightlight as NightlightIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Circle as CircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  AccessTime as AccessTimeIcon,
  LocalFireDepartment as FireIcon,
} from '@mui/icons-material';
import { useStrategyWebSocket } from '../hooks/useStrategyWebSocket';

// ==================== DESIGN TOKENS ====================
const COLORS = {
  primary: '#0ea5e9',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  purple: '#8b5cf6',
  gold: '#eab308',
  hot: '#ff6b35',
  neutral: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
};

// ==================== KEYFRAMES (inline via sx) ====================
const pulseKeyframes = {
  '@keyframes hotPulse': {
    '0%': { borderColor: alpha('#ff6b35', 0.6), boxShadow: `0 0 8px ${alpha('#ff6b35', 0.2)}` },
    '50%': { borderColor: alpha('#ff6b35', 1), boxShadow: `0 0 20px ${alpha('#ff6b35', 0.4)}` },
    '100%': { borderColor: alpha('#ff6b35', 0.6), boxShadow: `0 0 8px ${alpha('#ff6b35', 0.2)}` },
  },
};

// ==================== HELPER FUNCTIONS ====================

const getMessageText = (message) => {
  if (!message) return 'Update received';
  if (typeof message === 'string') return message;
  if (typeof message === 'object') {
    return message.message || message.signal_type || message.type || message.signal || 'Signal update received';
  }
  return 'Update received';
};

const getAlertIcon = (type) => {
  if (!type) return <InfoIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />;
  const normalizedType = type.toLowerCase();
  switch (normalizedType) {
    case 'success':
    case 'bulk_entry':
    case 'entry':
    case 'setup':
    case 'open':
      return <CheckCircleIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />;
    case 'warning':
    case 'early':
    case 'pending':
    case 'pending_update':
      return <WarningIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />;
    case 'error':
    case 'bulk_exit':
    case 'exit':
    case 'completed':
      return <ErrorIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />;
    case 'signal_ignored_locked':
    case 'substrategy_completed_locked':
      return <LockIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />;
    default:
      return <InfoIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />;
  }
};

const getAlertColor = (type) => {
  if (!type) return COLORS.primary;
  const normalizedType = type.toLowerCase();
  switch (normalizedType) {
    case 'success':
    case 'bulk_entry':
    case 'entry':
    case 'setup':
    case 'open':
      return COLORS.success;
    case 'warning':
    case 'early':
    case 'pending':
    case 'pending_update':
      return COLORS.warning;
    case 'error':
    case 'bulk_exit':
    case 'exit':
    case 'completed':
      return COLORS.error;
    case 'signal_ignored_locked':
    case 'substrategy_completed_locked':
      return COLORS.warning;
    default:
      return COLORS.primary;
  }
};

const getStatusColor = (status, completionPct = 0) => {
  switch (status?.toLowerCase()) {
    case 'open':
      return COLORS.success;
    case 'pending':
      return COLORS.neutral[500];
    case 'completed':
      return completionPct === 100 ? COLORS.success : COLORS.neutral[500];
    case 'idle':
      return COLORS.neutral[500];
    default:
      return COLORS.neutral[500];
  }
};

const getStatusIcon = (status) => {
  switch (status?.toLowerCase()) {
    case 'open':
      return <PlayArrowIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />;
    case 'pending':
      return <HourglassEmptyIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />;
    case 'completed':
      return <StopIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />;
    default:
      return <CircleIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />;
  }
};

const formatSignalTime = (timeStr) => {
  if (!timeStr) return '—';
  try {
    const date = new Date(timeStr);
    return date.toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
  } catch {
    return timeStr;
  }
};

const formatPrice = (price) => {
  if (price === null || price === undefined) return '—';
  return `$${Number(price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const getTradingHour = (strategy) => {
  if (strategy.trading_hour) return strategy.trading_hour;
  if (strategy.configuration?.exit_signal?.trading_hour) return strategy.configuration.exit_signal.trading_hour;
  if (strategy.current_state?.trading_hour) return strategy.current_state.trading_hour;
  return null;
};

const getDirection = (strategy) => {
  if (strategy.direction) return strategy.direction;
  if (strategy.configuration?.direction) return strategy.configuration.direction;
  if (strategy.strategy_config?.direction) return strategy.strategy_config.direction;
  return 'LONG';
};

const getStatus = (strategy) => {
  if (strategy.current_state?.status) return strategy.current_state.status;
  if (strategy.status) return strategy.status;
  if (strategy.current_state?.in_position) return 'open';
  return 'idle';
};

const getCompletionPercentage = (strategy) => {
  if (strategy.progress?.percentage !== undefined) {
    return strategy.progress.percentage;
  }
  if (strategy.current_state?.completion_percentage !== undefined) {
    return strategy.current_state.completion_percentage;
  }
  if (strategy.progress?.total > 0) {
    return (strategy.progress.completed / strategy.progress.total) * 100;
  }
  return 0;
};

const getTriggerSignal = (strategy) => {
  return strategy.current_state?.trigger_signal || 
         strategy.trigger_signal ||
         (getDirection(strategy) === 'LONG' ? 'cobra_long' : 'cobra_short');
};

// ==================== HOT STRATEGY HELPERS ====================

/**
 * Extract the missing signal info when only 1 confirmation is pending.
 * Returns { id, earlySignalName, confirmationSignalName, conditionType } or null.
 */
const getMissingSignalInfo = (pendingConfirmations, confirmations) => {
  if (!pendingConfirmations || pendingConfirmations.length !== 1) return null;
  
  const missingId = pendingConfirmations[0];
  const missingData = confirmations[missingId];
  
  if (!missingData) return null;
  
  return {
    id: missingId,
    earlySignalName: missingData.early_signal?.name?.toUpperCase() || 'UNKNOWN',
    confirmationSignalName: missingData.confirmation_signal?.name?.toUpperCase() || 'UNKNOWN',
    conditionType: missingData.type || 'unknown',
  };
};

// ==================== MAIN COMPONENT ====================

export const BulkStrategyCard = ({
  strategy,
  isExpanded,
  onExpandChange,
  onOpenDoc,
  liveUpdates = [],
  realtimeState,
  realtimeLoading,
  broadcasts = [],
  broadcastsLoading = false,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const { isConnected: wsConnected } = useStrategyWebSocket(
    '/',
    isExpanded ? strategy.bulk_strategy_id : null,
    null
  );

  // Derived state
  const currentState = strategy.current_state || {};
  const status = getStatus(strategy);
  const completionPct = getCompletionPercentage(strategy);
  const direction = getDirection(strategy);
  const tradingHour = getTradingHour(strategy);
  const triggerSignal = getTriggerSignal(strategy);

  const pendingConfirmations = currentState.pending_confirmations || [];
  const requiredSignals = currentState.required_signals || {};
  const confirmations = currentState.confirmations || {};

  // ==================== HOT STRATEGY DETECTION ====================
  const isPending = status === 'pending';
  const isOpen = status === 'open';
  const isComplete = completionPct === 100;
  
  // "Hot" = pending with exactly 1 signal left
  const isHotStrategy = isPending && pendingConfirmations.length === 1;
  const missingSignal = useMemo(
    () => getMissingSignalInfo(pendingConfirmations, confirmations),
    [pendingConfirmations, confirmations]
  );

  // Build sub-strategies
  const subStrategies = useMemo(() => {
    const subs = [];
    
    Object.entries(requiredSignals).forEach(([id, data]) => {
      subs.push({
        strategy_id: id,
        type: 'required',
        condition_type: data.type,
        condition: {
          type: data.type,
          early_signal: data.early_signal,
          confirmation_signal: data.confirmation_signal,
          min_minutes: data.min_minutes,
          max_minutes: data.max_minutes,
        },
        early_signal: data.early_signal?.name,
        confirmation_signal: data.confirmation_signal?.name,
        early_signal_data: data.early_signal,
        confirmation_signal_data: data.confirmation_signal,
        completed: data.early_signal?.time !== null && data.confirmation_signal?.time !== null,
      });
    });
    
    Object.entries(confirmations).forEach(([id, data]) => {
      const isPendingConf = pendingConfirmations.includes(id);
      subs.push({
        strategy_id: id,
        type: 'confirmation',
        condition_type: data.type,
        condition: {
          type: data.type,
          early_signal: data.early_signal,
          confirmation_signal: data.confirmation_signal,
          min_minutes: data.min_minutes,
          max_minutes: data.max_minutes,
        },
        early_signal: data.early_signal?.name,
        confirmation_signal: data.confirmation_signal?.name,
        early_signal_data: data.early_signal,
        confirmation_signal_data: data.confirmation_signal,
        completed: !isPendingConf,
        isPending: isPendingConf,
        isLastMissing: isHotStrategy && isPendingConf,
      });
    });
    
    if (subs.length === 0 && strategy.sub_strategies) {
      return strategy.sub_strategies;
    }
    
    return subs;
  }, [requiredSignals, confirmations, pendingConfirmations, strategy.sub_strategies, isHotStrategy]);

  const requiredCount = Object.keys(requiredSignals).length || strategy.configuration?.required_strategies?.length || 0;
  const confirmationCount = Object.keys(confirmations).length || strategy.configuration?.confirmation_strategies?.length || 0;
  const totalSignals = requiredCount + confirmationCount;
  const satisfiedSignals = totalSignals - pendingConfirmations.length;

  // Filter updates
  const strategyUpdates = useMemo(() => {
    return liveUpdates.filter((u) => {
      const directMatch = u.strategy_id === strategy.bulk_strategy_id || u.bulk_strategy_id === strategy.bulk_strategy_id;
      if (directMatch) return true;
      if (u.message) {
        if (typeof u.message === 'string') {
          return u.message.includes(strategy.bulk_strategy_id);
        } else if (typeof u.message === 'object') {
          return u.message.bulk_strategy_id === strategy.bulk_strategy_id || u.message.strategy_id === strategy.bulk_strategy_id;
        }
      }
      return false;
    });
  }, [liveUpdates, strategy.bulk_strategy_id]);

  const mostRecentUpdate = strategyUpdates?.[0];
  const unreadCount = strategyUpdates.length;

  // Card border color
  const getBorderColor = () => {
    if (isHotStrategy) return COLORS.hot;
    if (isOpen) return COLORS.success;
    if (isComplete) return COLORS.success;
    return COLORS.neutral[700];
  };

  const primaryColor = isHotStrategy ? COLORS.hot : isOpen ? COLORS.success : isComplete ? COLORS.success : COLORS.primary;

  // Related alerts helper
  const getRelatedAlerts = (subStrategy) => {
    const searchTerms = [subStrategy.strategy_id, subStrategy.early_signal, subStrategy.confirmation_signal].filter(Boolean);
    return strategyUpdates.filter((u) =>
      searchTerms.some((term) => {
        if (u.signal === term) return true;
        if (u.message && typeof u.message === 'string') {
          return u.message.toLowerCase().includes(term.toLowerCase());
        }
        return false;
      })
    );
  };

  return (
    <Card
      sx={{
        ...pulseKeyframes,
        background: `linear-gradient(135deg, ${COLORS.neutral[900]} 0%, ${COLORS.neutral[800]} 100%)`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${getBorderColor()}`,
        borderRadius: { xs: 2, sm: 3 },
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: isHotStrategy
          ? `0 4px 24px ${alpha(COLORS.hot, 0.25)}`
          : isOpen 
            ? `0 4px 24px ${alpha(COLORS.success, 0.2)}`
            : `0 4px 16px ${alpha('#000', 0.3)}`,
        overflow: 'hidden',
        ...(isHotStrategy && {
          animation: 'hotPulse 2s ease-in-out infinite',
        }),
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isHotStrategy
            ? `0 8px 32px ${alpha(COLORS.hot, 0.35)}`
            : isOpen 
              ? `0 8px 32px ${alpha(COLORS.success, 0.25)}`
              : `0 8px 24px ${alpha('#000', 0.4)}`,
        },
      }}
    >
      {/* Status Indicator Bar */}
      {(isOpen || isHotStrategy) && (
        <Box
          sx={{
            height: 4,
            background: isHotStrategy
              ? `linear-gradient(90deg, ${COLORS.hot} 0%, ${COLORS.warning} 50%, ${COLORS.hot} 100%)`
              : `linear-gradient(90deg, ${COLORS.success} 0%, ${alpha(COLORS.success, 0.6)} 100%)`,
            ...(isHotStrategy && {
              backgroundSize: '200% 100%',
              animation: 'hotPulse 2s ease-in-out infinite',
            }),
          }}
        />
      )}

      {/* 🔥 HOT STRATEGY BANNER - 1 Signal Away */}
      {isHotStrategy && missingSignal && (
        <Box
          sx={{
            background: `linear-gradient(135deg, ${alpha(COLORS.hot, 0.18)} 0%, ${alpha(COLORS.warning, 0.1)} 100%)`,
            borderBottom: `1px solid ${alpha(COLORS.hot, 0.3)}`,
            px: { xs: 2, sm: 2.5 },
            py: { xs: 1.5, sm: 2 },
          }}
        >
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            alignItems={{ xs: 'flex-start', sm: 'center' }} 
            justifyContent="space-between"
            spacing={{ xs: 1, sm: 2 }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: { xs: 32, sm: 40 },
                  height: { xs: 32, sm: 40 },
                  borderRadius: '50%',
                  bgcolor: alpha(COLORS.hot, 0.2),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FireIcon sx={{ color: COLORS.hot, fontSize: { xs: 18, sm: 22 } }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: COLORS.hot,
                    fontWeight: 700,
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    letterSpacing: '0.02em',
                  }}
                >
                  1 SIGNAL AWAY
                </Typography>
                <Typography
                  sx={{
                    color: alpha('#fff', 0.7),
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    mt: 0.25,
                  }}
                >
                  Waiting: <strong style={{ color: COLORS.warning }}>{missingSignal.earlySignalName}</strong>
                  {' → '}
                  <strong style={{ color: COLORS.hot }}>{missingSignal.confirmationSignalName}</strong>
                </Typography>
              </Box>
            </Stack>
            <Chip
              label={`${satisfiedSignals}/${totalSignals} SIGNALS`}
              size="small"
              sx={{
                bgcolor: alpha(COLORS.hot, 0.15),
                color: COLORS.hot,
                fontWeight: 700,
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                height: { xs: 26, sm: 28 },
                border: `1px solid ${alpha(COLORS.hot, 0.3)}`,
              }}
            />
          </Stack>
        </Box>
      )}

      {/* Open Position Banner */}
      {isOpen && (
        <Box
          sx={{
            background: `linear-gradient(135deg, ${alpha(COLORS.success, 0.15)} 0%, ${alpha(COLORS.success, 0.08)} 100%)`,
            borderBottom: `1px solid ${alpha(COLORS.success, 0.2)}`,
            px: { xs: 2, sm: 2.5 },
            py: { xs: 1.5, sm: 2 },
          }}
        >
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            alignItems={{ xs: 'flex-start', sm: 'center' }} 
            justifyContent="space-between"
            spacing={{ xs: 1, sm: 2 }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: { xs: 32, sm: 40 },
                  height: { xs: 32, sm: 40 },
                  borderRadius: '50%',
                  bgcolor: alpha(COLORS.success, 0.2),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <BoltIcon sx={{ color: COLORS.success, fontSize: { xs: 18, sm: 22 } }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: COLORS.success,
                    fontWeight: 700,
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    letterSpacing: '0.02em',
                  }}
                >
                  POSITION ACTIVE
                </Typography>
                <Typography
                  sx={{
                    color: alpha('#fff', 0.6),
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    mt: 0.25,
                  }}
                >
                  Entry: {formatPrice(currentState.entry_price)} • {formatSignalTime(currentState.entry_time)}
                </Typography>
              </Box>
            </Stack>
            <Chip
              label={direction}
              icon={direction === 'LONG' ? <TrendingUpIcon /> : <TrendingDownIcon />}
              size="small"
              sx={{
                bgcolor: alpha(direction === 'LONG' ? COLORS.success : COLORS.error, 0.15),
                color: direction === 'LONG' ? COLORS.success : COLORS.error,
                fontWeight: 700,
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                height: { xs: 26, sm: 28 },
                border: `1px solid ${alpha(direction === 'LONG' ? COLORS.success : COLORS.error, 0.3)}`,
                '& .MuiChip-icon': {
                  color: direction === 'LONG' ? COLORS.success : COLORS.error,
                  fontSize: { xs: 14, sm: 16 },
                },
              }}
            />
          </Stack>
        </Box>
      )}

      {/* Recent Update Banner */}
      {mostRecentUpdate && (
        <Box
          sx={{
            bgcolor: alpha(getAlertColor(mostRecentUpdate.type), 0.08),
            borderBottom: `1px solid ${alpha(getAlertColor(mostRecentUpdate.type), 0.15)}`,
            px: { xs: 2, sm: 2.5 },
            py: { xs: 1, sm: 1.25 },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ color: getAlertColor(mostRecentUpdate.type), display: 'flex' }}>
              {getAlertIcon(mostRecentUpdate.type)}
            </Box>
            <Box flex={1} minWidth={0}>
              <Typography
                sx={{
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', sm: '0.8rem' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {getMessageText(mostRecentUpdate.message)}
              </Typography>
              <Typography
                sx={{
                  color: alpha('#fff', 0.5),
                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                }}
              >
                {new Date(mostRecentUpdate.timestamp).toISOString().slice(11, 19)} UTC
              </Typography>
            </Box>
            {unreadCount > 1 && (
              <Chip
                label={`+${unreadCount - 1}`}
                size="small"
                sx={{
                  bgcolor: alpha(getAlertColor(mostRecentUpdate.type), 0.2),
                  color: getAlertColor(mostRecentUpdate.type),
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  height: 22,
                  minWidth: 32,
                }}
              />
            )}
          </Stack>
        </Box>
      )}

      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header Section */}
        <Box sx={{ mb: { xs: 2, sm: 2.5 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
            spacing={{ xs: 2, sm: 0 }}
          >
            {/* Left: Avatar + Title + Tags */}
            <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="flex-start" flex={1} minWidth={0}>
              <Avatar
                sx={{
                  width: { xs: 40, sm: 48, md: 52 },
                  height: { xs: 40, sm: 48, md: 52 },
                  bgcolor: alpha(primaryColor, 0.15),
                  border: `1px solid ${alpha(primaryColor, 0.25)}`,
                  flexShrink: 0,
                }}
              >
                {isHotStrategy ? (
                  <FireIcon sx={{ fontSize: { xs: 20, sm: 24, md: 26 }, color: primaryColor }} />
                ) : (
                  <DashboardIcon sx={{ fontSize: { xs: 20, sm: 24, md: 26 }, color: primaryColor }} />
                )}
              </Avatar>
              <Box flex={1} minWidth={0}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                  <Typography
                    sx={{
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {strategy.bulk_strategy_id}
                  </Typography>
                  <Chip
                    icon={getStatusIcon(status)}
                    label={status.toUpperCase()}
                    size="small"
                    sx={{
                      bgcolor: alpha(getStatusColor(status, completionPct), 0.15),
                      color: getStatusColor(status, completionPct),
                      fontWeight: 700,
                      height: { xs: 20, sm: 22 },
                      fontSize: { xs: '0.6rem', sm: '0.65rem' },
                      '& .MuiChip-icon': {
                        color: getStatusColor(status, completionPct),
                      },
                    }}
                  />
                  {isHotStrategy && (
                    <Chip
                      icon={<FireIcon sx={{ fontSize: '12px !important' }} />}
                      label="HOT"
                      size="small"
                      sx={{
                        bgcolor: alpha(COLORS.hot, 0.2),
                        color: COLORS.hot,
                        fontWeight: 700,
                        height: { xs: 20, sm: 22 },
                        fontSize: { xs: '0.6rem', sm: '0.65rem' },
                        '& .MuiChip-icon': {
                          color: COLORS.hot,
                        },
                      }}
                    />
                  )}
                </Stack>
                
                {/* Tags Row */}
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={strategy.symbol || 'NQ'}
                    size="small"
                    sx={{
                      bgcolor: alpha(COLORS.primary, 0.12),
                      color: COLORS.primary,
                      fontWeight: 600,
                      height: { xs: 20, sm: 22 },
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                    }}
                  />
                  <Chip
                    label={strategy.timeframe || '1m'}
                    size="small"
                    sx={{
                      bgcolor: alpha(COLORS.purple, 0.12),
                      color: COLORS.purple,
                      fontWeight: 600,
                      height: { xs: 20, sm: 22 },
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                    }}
                  />
                  {tradingHour && (
                    <Chip
                      icon={tradingHour === 'RTH' ? <WbSunnyIcon /> : <NightlightIcon />}
                      label={tradingHour}
                      size="small"
                      sx={{
                        bgcolor: alpha(tradingHour === 'RTH' ? COLORS.gold : COLORS.purple, 0.12),
                        color: tradingHour === 'RTH' ? COLORS.gold : COLORS.purple,
                        fontWeight: 600,
                        height: { xs: 20, sm: 22 },
                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                        '& .MuiChip-icon': {
                          color: tradingHour === 'RTH' ? COLORS.gold : COLORS.purple,
                          fontSize: { xs: 12, sm: 14 },
                        },
                      }}
                    />
                  )}
                  {!isOpen && (
                    <Chip
                      icon={direction === 'LONG' ? <TrendingUpIcon /> : <TrendingDownIcon />}
                      label={direction}
                      size="small"
                      sx={{
                        bgcolor: alpha(direction === 'LONG' ? COLORS.success : COLORS.error, 0.12),
                        color: direction === 'LONG' ? COLORS.success : COLORS.error,
                        fontWeight: 600,
                        height: { xs: 20, sm: 22 },
                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                        '& .MuiChip-icon': {
                          color: direction === 'LONG' ? COLORS.success : COLORS.error,
                          fontSize: { xs: 12, sm: 14 },
                        },
                      }}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>

            {/* Right: Action Buttons */}
            <Stack 
              direction="row" 
              spacing={1} 
              alignItems="center"
              sx={{ 
                flexShrink: 0,
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'flex-end', sm: 'flex-start' },
              }}
            >
              {isExpanded && (
                <Tooltip title={wsConnected ? 'Live updates active' : 'Connecting...'}>
                  <Chip
                    size="small"
                    icon={<CircleIcon sx={{ fontSize: '8px !important' }} />}
                    label="LIVE"
                    sx={{
                      height: { xs: 22, sm: 24 },
                      fontSize: { xs: '0.6rem', sm: '0.65rem' },
                      bgcolor: alpha(wsConnected ? COLORS.success : COLORS.neutral[500], 0.15),
                      color: wsConnected ? COLORS.success : COLORS.neutral[500],
                      fontWeight: 600,
                      '& .MuiChip-icon': {
                        color: wsConnected ? COLORS.success : COLORS.neutral[500],
                      },
                    }}
                  />
                </Tooltip>
              )}

              <Tooltip title="Trade History">
                <IconButton
                  size="small"
                  onClick={() => navigate(`/officer/trade-history?strategy=${strategy.bulk_strategy_id}`)}
                  sx={{
                    border: `1px solid ${alpha(COLORS.primary, 0.3)}`,
                    color: COLORS.primary,
                    width: { xs: 32, sm: 36 },
                    height: { xs: 32, sm: 36 },
                    '&:hover': {
                      borderColor: COLORS.primary,
                      bgcolor: alpha(COLORS.primary, 0.1),
                    },
                  }}
                >
                  <HistoryIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                </IconButton>
              </Tooltip>

              <Button
                variant="outlined"
                size="small"
                endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => onExpandChange(strategy.bulk_strategy_id)}
                sx={{
                  borderColor: alpha(COLORS.purple, 0.3),
                  color: COLORS.purple,
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  px: { xs: 1.5, sm: 2 },
                  height: { xs: 32, sm: 36 },
                  minWidth: { xs: 70, sm: 80 },
                  '&:hover': {
                    borderColor: COLORS.purple,
                    bgcolor: alpha(COLORS.purple, 0.1),
                  },
                }}
              >
                {isExpanded ? 'Hide' : 'Details'}
              </Button>

              <Tooltip title="View Payload">
                <IconButton
                  size="small"
                  onClick={() => onOpenDoc(strategy)}
                  sx={{
                    border: `1px solid ${alpha(COLORS.primary, 0.3)}`,
                    color: COLORS.primary,
                    width: { xs: 32, sm: 36 },
                    height: { xs: 32, sm: 36 },
                    '&:hover': {
                      borderColor: COLORS.primary,
                      bgcolor: alpha(COLORS.primary, 0.1),
                    },
                  }}
                >
                  <DescriptionIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>

        {/* Progress Section */}
        <Box sx={{ mb: { xs: 2, sm: 2.5 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography
              sx={{
                color: isHotStrategy ? COLORS.hot : COLORS.neutral[400],
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {isHotStrategy ? '🔥 Almost There' : 'Progress'}
            </Typography>
            <Typography
              sx={{
                color: isHotStrategy ? COLORS.hot : isComplete ? COLORS.success : COLORS.neutral[300],
                fontWeight: 700,
                fontSize: { xs: '0.8rem', sm: '0.85rem' },
              }}
            >
              {isPending
                ? `${satisfiedSignals} / ${totalSignals} signals`
                : strategy.progress
                  ? `${strategy.progress.completed} / ${strategy.progress.total}`
                  : `${completionPct.toFixed(0)}%`}
            </Typography>
          </Stack>
          <Box sx={{ position: 'relative' }}>
            <LinearProgress
              variant="determinate"
              value={completionPct}
              sx={{
                height: { xs: 6, sm: 8 },
                borderRadius: 4,
                bgcolor: alpha(COLORS.neutral[700], 0.5),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: isHotStrategy
                    ? `linear-gradient(90deg, ${COLORS.hot} 0%, ${COLORS.warning} 100%)`
                    : isComplete
                      ? `linear-gradient(90deg, ${COLORS.success} 0%, ${alpha(COLORS.success, 0.8)} 100%)`
                      : `linear-gradient(90deg, ${COLORS.primary} 0%, ${alpha(COLORS.primary, 0.7)} 100%)`,
                },
              }}
            />
          </Box>
        </Box>

        {/* Stats Grid */}
        <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: { xs: 2, sm: 2.5 } }}>
          <Grid item xs={4}>
            <Box
              sx={{
                p: { xs: 1.25, sm: 1.5 },
                bgcolor: alpha(COLORS.primary, 0.08),
                borderRadius: { xs: 1.5, sm: 2 },
                border: `1px solid ${alpha(COLORS.primary, 0.15)}`,
                height: '100%',
              }}
            >
              <Typography
                sx={{
                  color: COLORS.neutral[400],
                  fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                  mb: 0.5,
                  fontWeight: 500,
                }}
              >
                Required
              </Typography>
              <Typography
                sx={{
                  color: COLORS.primary,
                  fontWeight: 700,
                  fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                  lineHeight: 1.2,
                }}
              >
                {requiredCount}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box
              sx={{
                p: { xs: 1.25, sm: 1.5 },
                bgcolor: alpha(COLORS.gold, 0.08),
                borderRadius: { xs: 1.5, sm: 2 },
                border: `1px solid ${alpha(COLORS.gold, 0.15)}`,
                height: '100%',
              }}
            >
              <Typography
                sx={{
                  color: COLORS.neutral[400],
                  fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                  mb: 0.5,
                  fontWeight: 500,
                }}
              >
                Confirmations
              </Typography>
              <Typography
                sx={{
                  color: COLORS.gold,
                  fontWeight: 700,
                  fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                  lineHeight: 1.2,
                }}
              >
                {confirmationCount}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box
              sx={{
                p: { xs: 1.25, sm: 1.5 },
                bgcolor: alpha(isHotStrategy ? COLORS.hot : COLORS.neutral[500], 0.08),
                borderRadius: { xs: 1.5, sm: 2 },
                border: `1px solid ${alpha(isHotStrategy ? COLORS.hot : COLORS.neutral[500], 0.15)}`,
                height: '100%',
              }}
            >
              <Typography
                sx={{
                  color: COLORS.neutral[400],
                  fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                  mb: 0.5,
                  fontWeight: 500,
                }}
              >
                Pending
              </Typography>
              <Typography
                sx={{
                  color: isHotStrategy ? COLORS.hot : COLORS.neutral[300],
                  fontWeight: 700,
                  fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                  lineHeight: 1.2,
                }}
              >
                {pendingConfirmations.length}
                {isHotStrategy && ' 🔥'}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Trigger Signal - Only for Pending status */}
        {isPending && triggerSignal && (
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              mb: { xs: 2, sm: 2.5 },
              bgcolor: alpha(COLORS.neutral[700], 0.3),
              borderRadius: { xs: 1.5, sm: 2 },
              border: `1px solid ${alpha(COLORS.neutral[600], 0.3)}`,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  borderRadius: '50%',
                  bgcolor: alpha(COLORS.primary, 0.15),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <BoltIcon sx={{ color: COLORS.primary, fontSize: { xs: 18, sm: 20 } }} />
              </Box>
              <Box flex={1} minWidth={0}>
                <Typography
                  sx={{
                    color: COLORS.neutral[400],
                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Trigger Signal
                </Typography>
                <Typography
                  sx={{
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {triggerSignal.toUpperCase()}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}

        {/* Open Position Details */}
        {isOpen && currentState.entry_time && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2 },
              mb: { xs: 2, sm: 2.5 },
              bgcolor: alpha(COLORS.success, 0.08),
              border: `1px solid ${alpha(COLORS.success, 0.2)}`,
              borderRadius: { xs: 1.5, sm: 2 },
            }}
          >
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={6}>
                <Typography
                  sx={{
                    color: COLORS.neutral[400],
                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                    mb: 0.5,
                  }}
                >
                  Entry Time
                </Typography>
                <Typography
                  sx={{
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  }}
                >
                  {formatSignalTime(currentState.entry_time)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  sx={{
                    color: COLORS.neutral[400],
                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                    mb: 0.5,
                  }}
                >
                  Entry Price
                </Typography>
                <Typography
                  sx={{
                    color: COLORS.success,
                    fontWeight: 700,
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                  }}
                >
                  {formatPrice(currentState.entry_price)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Expanded Content */}
        <Collapse in={isExpanded} timeout="auto">
          <Box
            sx={{
              mt: { xs: 1.5, sm: 2 },
              pt: { xs: 1.5, sm: 2 },
              borderTop: `1px solid ${alpha(COLORS.neutral[600], 0.3)}`,
            }}
          >
            {/* Activity Log */}
            {strategyUpdates.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    color: COLORS.primary,
                    fontWeight: 600,
                    fontSize: { xs: '0.8rem', sm: '0.85rem' },
                    mb: 1.5,
                  }}
                >
                  Activity Log ({strategyUpdates.length})
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: alpha(COLORS.neutral[800], 0.5),
                    border: `1px solid ${alpha(COLORS.neutral[600], 0.2)}`,
                    borderRadius: 2,
                    maxHeight: { xs: 200, sm: 250 },
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                      width: 6,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      bgcolor: alpha(COLORS.neutral[500], 0.5),
                      borderRadius: 3,
                    },
                  }}
                >
                  <List dense disablePadding>
                    {strategyUpdates.slice(0, 20).map((update, idx) => (
                      <ListItem
                        key={update.id || idx}
                        sx={{
                          py: { xs: 1, sm: 1.25 },
                          px: { xs: 1.5, sm: 2 },
                          borderBottom: `1px solid ${alpha(COLORS.neutral[600], 0.15)}`,
                          '&:last-child': { borderBottom: 'none' },
                          '&:hover': {
                            bgcolor: alpha(getAlertColor(update.type), 0.05),
                          },
                        }}
                      >
                        <Box
                          sx={{
                            mr: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            color: getAlertColor(update.type),
                          }}
                        >
                          {getAlertIcon(update.type)}
                        </Box>
                        <ListItemText
                          primary={
                            <Typography
                              sx={{
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                              }}
                            >
                              {getMessageText(update.message)}
                            </Typography>
                          }
                          secondary={
                            <Typography
                              sx={{
                                color: COLORS.neutral[400],
                                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                              }}
                            >
                              {new Date(update.timestamp).toISOString().replace('T', ' ').slice(0, 19)} UTC
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            )}

            {/* Sub-Strategies Status */}
            <Typography
              sx={{
                color: '#fff',
                fontWeight: 600,
                fontSize: { xs: '0.8rem', sm: '0.85rem' },
                mb: 2,
              }}
            >
              Sub-Strategies ({subStrategies.length})
            </Typography>

            <Stack spacing={{ xs: 1.5, sm: 2 }}>
              {subStrategies.map((subStrategy) => {
                const subStrategyId = subStrategy.strategy_id;
                const isConfirmation = subStrategy.type === 'confirmation';
                const isRequired = subStrategy.type === 'required';
                const isSubCompleted = subStrategy.completed;
                const isSubPending = subStrategy.isPending;
                const isLastMissing = subStrategy.isLastMissing;

                const subStatusColor = isSubCompleted 
                  ? COLORS.success 
                  : isLastMissing 
                    ? COLORS.hot 
                    : COLORS.neutral[500];

                return (
                  <Paper
                    key={subStrategyId}
                    elevation={0}
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      bgcolor: alpha(COLORS.neutral[800], 0.4),
                      border: `1px solid ${alpha(
                        isSubCompleted ? COLORS.success : isLastMissing ? COLORS.hot : COLORS.neutral[600], 
                        isLastMissing ? 0.4 : 0.2
                      )}`,
                      borderRadius: 2,
                      ...(isLastMissing && {
                        bgcolor: alpha(COLORS.hot, 0.05),
                      }),
                    }}
                  >
                    {/* Sub-Strategy Header */}
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      spacing={1}
                      sx={{ mb: 2 }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        {isSubCompleted ? (
                          <CheckCircleIcon sx={{ color: COLORS.success, fontSize: { xs: 16, sm: 18 } }} />
                        ) : isLastMissing ? (
                          <FireIcon sx={{ color: COLORS.hot, fontSize: { xs: 16, sm: 18 } }} />
                        ) : (
                          <HourglassEmptyIcon sx={{ color: COLORS.neutral[400], fontSize: { xs: 16, sm: 18 } }} />
                        )}
                        <Typography
                          sx={{
                            color: isLastMissing ? COLORS.hot : '#fff',
                            fontWeight: 600,
                            fontSize: { xs: '0.8rem', sm: '0.85rem' },
                          }}
                        >
                          {subStrategyId}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <Chip
                          label={isRequired ? 'REQUIRED' : 'CONFIRMATION'}
                          size="small"
                          sx={{
                            bgcolor: alpha(isRequired ? COLORS.primary : COLORS.gold, 0.12),
                            color: isRequired ? COLORS.primary : COLORS.gold,
                            fontWeight: 600,
                            height: { xs: 18, sm: 20 },
                            fontSize: { xs: '0.55rem', sm: '0.6rem' },
                          }}
                        />
                        <Chip
                          label={subStrategy.condition_type?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN'}
                          size="small"
                          sx={{
                            bgcolor: alpha(COLORS.purple, 0.12),
                            color: COLORS.purple,
                            fontWeight: 600,
                            height: { xs: 18, sm: 20 },
                            fontSize: { xs: '0.55rem', sm: '0.6rem' },
                          }}
                        />
                        <Chip
                          label={isSubCompleted ? 'SATISFIED' : isLastMissing ? '🔥 LAST NEEDED' : 'PENDING'}
                          size="small"
                          sx={{
                            bgcolor: alpha(subStatusColor, 0.15),
                            color: subStatusColor,
                            fontWeight: 700,
                            height: { xs: 18, sm: 20 },
                            fontSize: { xs: '0.55rem', sm: '0.6rem' },
                          }}
                        />
                      </Stack>
                    </Stack>

                    {/* Signal Details */}
                    <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: { xs: 1, sm: 1.25 },
                            bgcolor: alpha(COLORS.primary, 0.06),
                            borderRadius: 1.5,
                            border: `1px solid ${alpha(COLORS.primary, 0.12)}`,
                            height: '100%',
                          }}
                        >
                          <Typography
                            sx={{
                              color: COLORS.neutral[400],
                              fontSize: { xs: '0.55rem', sm: '0.6rem' },
                              textTransform: 'uppercase',
                              letterSpacing: '0.03em',
                              mb: 0.5,
                            }}
                          >
                            Early Signal
                          </Typography>
                          <Typography
                            sx={{
                              color: COLORS.primary,
                              fontWeight: 700,
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                              mb: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {subStrategy.early_signal_data?.name?.toUpperCase() || subStrategy.early_signal?.toUpperCase() || '—'}
                          </Typography>
                          {subStrategy.early_signal_data?.time ? (
                            <>
                              <Typography
                                sx={{
                                  color: COLORS.neutral[400],
                                  fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                }}
                              >
                                {formatSignalTime(subStrategy.early_signal_data.time)}
                              </Typography>
                              <Typography
                                sx={{
                                  color: COLORS.primary,
                                  fontWeight: 600,
                                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                }}
                              >
                                {formatPrice(subStrategy.early_signal_data.price)}
                              </Typography>
                            </>
                          ) : (
                            <Typography
                              sx={{
                                color: isLastMissing ? COLORS.hot : COLORS.neutral[500],
                                fontStyle: 'italic',
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                fontWeight: isLastMissing ? 600 : 400,
                              }}
                            >
                              {isLastMissing ? '⏳ Awaiting...' : 'Awaiting signal'}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            p: { xs: 1, sm: 1.25 },
                            bgcolor: alpha(COLORS.success, 0.06),
                            borderRadius: 1.5,
                            border: `1px solid ${alpha(COLORS.success, 0.12)}`,
                            height: '100%',
                          }}
                        >
                          <Typography
                            sx={{
                              color: COLORS.neutral[400],
                              fontSize: { xs: '0.55rem', sm: '0.6rem' },
                              textTransform: 'uppercase',
                              letterSpacing: '0.03em',
                              mb: 0.5,
                            }}
                          >
                            Confirmation
                          </Typography>
                          <Typography
                            sx={{
                              color: COLORS.success,
                              fontWeight: 700,
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                              mb: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {subStrategy.confirmation_signal_data?.name?.toUpperCase() || subStrategy.confirmation_signal?.toUpperCase() || '—'}
                          </Typography>
                          {subStrategy.confirmation_signal_data?.time ? (
                            <>
                              <Typography
                                sx={{
                                  color: COLORS.neutral[400],
                                  fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                }}
                              >
                                {formatSignalTime(subStrategy.confirmation_signal_data.time)}
                              </Typography>
                              <Typography
                                sx={{
                                  color: COLORS.success,
                                  fontWeight: 600,
                                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                }}
                              >
                                {formatPrice(subStrategy.confirmation_signal_data.price)}
                              </Typography>
                            </>
                          ) : (
                            <Typography
                              sx={{
                                color: isLastMissing ? COLORS.hot : COLORS.neutral[500],
                                fontStyle: 'italic',
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                fontWeight: isLastMissing ? 600 : 400,
                              }}
                            >
                              {isLastMissing ? '⏳ Awaiting...' : 'Awaiting signal'}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Time Constraints */}
                    {(subStrategy.condition?.min_minutes !== undefined || subStrategy.condition?.max_minutes !== undefined) && (
                      <Box
                        sx={{
                          mt: { xs: 1, sm: 1.5 },
                          p: { xs: 0.75, sm: 1 },
                          bgcolor: alpha(COLORS.purple, 0.06),
                          borderRadius: 1,
                          border: `1px solid ${alpha(COLORS.purple, 0.12)}`,
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <AccessTimeIcon sx={{ color: COLORS.purple, fontSize: { xs: 14, sm: 16 } }} />
                          <Typography
                            sx={{
                              color: COLORS.purple,
                              fontWeight: 600,
                              fontSize: { xs: '0.65rem', sm: '0.7rem' },
                            }}
                          >
                            Window: {subStrategy.condition.min_minutes || 0} - {subStrategy.condition.max_minutes || '∞'} min
                          </Typography>
                        </Stack>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Stack>

            {subStrategies.length === 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 4 },
                  textAlign: 'center',
                  bgcolor: alpha(COLORS.neutral[800], 0.3),
                  border: `1px dashed ${alpha(COLORS.neutral[600], 0.3)}`,
                  borderRadius: 2,
                }}
              >
                <Typography sx={{ color: COLORS.neutral[500], fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                  No sub-strategy data available
                </Typography>
              </Paper>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default BulkStrategyCard;
